import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Truck, User, CheckCircle, ArrowLeft, FileText, X, ShieldCheck, Bell } from 'lucide-react';
import logo from '@/assets/599ad72d49c8887573a4750887df95ce60e36150.png';
import { API_CONFIG, setAuthToken } from '../config/api';
import { apiService } from '../services/apiService';
import { setStoredDriverMobileUser } from '../utils/driverMobileSession';
import transportContractDefault from '@/assets/contracts/transport_contract.txt?raw';
import explicitConsentDefault from '@/assets/contracts/explicit_consent.txt?raw';
import electronicConsentDefault from '@/assets/contracts/electronic_consent.txt?raw';

interface RegistrationForm {
  phoneNumber: string;
  fullName: string;
  tcNo: string;
  tractorPlate: string;
  trailerPlate: string;
  trailerType: string;
}

interface Consents {
  contract: boolean;
  explicit: boolean;
  electronic: boolean;
}

type ConsentModalType = 'contract' | 'explicit' | 'electronic' | null;

const TRAILER_TYPES = [
  { value: 'tenteli', label: 'Tenteli', icon: '🚚' },
  { value: 'kayar-perde', label: 'Kayar perde', icon: '🚛' },
  { value: 'kapali-kasa', label: 'Kapalı kasa', icon: '📦' },
  { value: 'acik-kasa', label: 'Açık kasa', icon: '🔓' },
  { value: 'frigo', label: 'Frigo', icon: '❄️' },
  { value: 'lowbed', label: 'Lowbed', icon: '🏗️' },
  { value: 'tanker', label: 'Tanker', icon: '🛢️' },
  { value: 'damper', label: 'Damper', icon: '🚜' },
  { value: 'silobas', label: 'Silobas', icon: '🌾' },
];

const CONSENT_META: Record<Exclude<ConsentModalType, null>, { title: string; icon: typeof FileText }> = {
  contract: { title: 'Yurt İçi Taşımacılık Hizmet Sözleşmesi', icon: FileText },
  explicit: { title: 'Açık Rıza Metni', icon: ShieldCheck },
  electronic: { title: 'Ticari Elektronik İleti Onayı', icon: Bell },
};

const ELECTRONIC_CHANNELS = [
  { value: 'sms', label: 'SMS / Kısa Mesaj' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

export function DriverMobileRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState<RegistrationForm>({
    phoneNumber: '',
    fullName: '',
    tcNo: '',
    tractorPlate: '',
    trailerPlate: '',
    trailerType: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contract, setContract] = useState(transportContractDefault);
  const [explicitConsentText, setExplicitConsentText] = useState(explicitConsentDefault);
  const [electronicConsentText, setElectronicConsentText] = useState(electronicConsentDefault);
  const [consents, setConsents] = useState<Consents>({ contract: false, explicit: false, electronic: false });
  const [electronicChannels, setElectronicChannels] = useState<string[]>([]);
  const [activeModal, setActiveModal] = useState<ConsentModalType>(null);

  useEffect(() => {
    const phone = sessionStorage.getItem('driverRegisterPhone');

    if (phone) {
      setForm(prev => ({ ...prev, phoneNumber: phone }));
    } else {
      navigate('/driver-mobile-auth');
    }

    apiService
      .get<any>('/settings/public/driver_registration_contract')
      .then((response) => {
        const storedContract = response.data?.setting_value || '';
        if (storedContract) setContract(storedContract);
      })
      .catch((error) => {
        console.error('Failed to load driver registration contract:', error);
      });

    apiService
      .get<any>('/settings/public/driver_explicit_consent_text')
      .then((response) => {
        const storedConsent = response.data?.setting_value || '';
        if (storedConsent) setExplicitConsentText(storedConsent);
      })
      .catch((error) => {
        console.error('Failed to load explicit consent text:', error);
      });

    apiService
      .get<any>('/settings/public/driver_electronic_consent_text')
      .then((response) => {
        const storedConsent = response.data?.setting_value || '';
        if (storedConsent) setElectronicConsentText(storedConsent);
      })
      .catch((error) => {
        console.error('Failed to load electronic consent text:', error);
      });
  }, [navigate]);

  const handleChange = (field: keyof RegistrationForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const personalizeText = (text: string) => {
    let result = text;
    const name = form.fullName.trim();
    const tc = form.tcNo.trim();
    const phone = form.phoneNumber.trim();
    const today = new Date().toLocaleDateString('tr-TR');

    if (tc) {
      result = result.replace(/X\s+TC\s*\/\s*VKN\s*no'lu/gi, `${tc} TC/VKN no'lu`);
    }
    if (name) {
      result = result.replace(/X\s+adresinde\s+mukim\s*X/gi, `${name} adresinde mukim ${name}`);
    }
    if (name) {
      result = result.replace(/^Ad\s+Soyadı?\s*[:：]\s*/gim, (match) => `${match}${name} `);
    }
    result = result.replace(/^Tarih\s*[:：]\s*/gim, (match) => `${match}${today} `);
    if (phone) {
      result = result.replace(/^Telefon\s+Numarası\s*\/\s*Elektronik\s+İletişim\s+Adresi\s*[:：]\s*/gim, (match) => `${match}+90 ${phone} `);
      result = result.replace(/^Telefon\s+Numarası\s*[:：]\s*/gim, (match) => `${match}+90 ${phone} `);
    }
    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);

  if (form.tcNo.length !== 11) {
    alert('TC Kimlik No 11 haneli olmalıdır');
    setLoading(false);
    return;
  }

  try {
    const res = await fetch(`${API_CONFIG.BASE_URL}/auth/driver-register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fullName: form.fullName,
        email: `${form.phoneNumber}@driver.com`,
        phone: form.phoneNumber,
        password: "123456", // 🔥 geçici
        tcNumber: form.tcNo,
        cekiciPlaka: form.tractorPlate,
        dorsePlaka: form.trailerPlate,
        vehicleType: form.trailerType,
        consents: {
          registrationContract: consents.contract,
          explicitConsent: consents.explicit,
          electronicConsent: consents.electronic,
          electronicChannels,
        },
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message);
    }

    console.log("✅ KAYIT OK:", data);

    if (data?.data?.token && data?.data?.user) {
      setAuthToken(data.data.token);
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('currentUser', JSON.stringify(data.data.user));
      sessionStorage.setItem('sessionUser', JSON.stringify(data.data.user));
      setStoredDriverMobileUser(data.data.user);
    }

    setSubmitted(true);

  } catch (err: any) {
    console.error(err);
    alert(err.message || "Kayıt hatası");
  }

  setLoading(false);
};

  if (submitted) {
    return (
      <div className="safe-area-page min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Kayıt Başarılı!</h2>
          <p className="text-gray-600 mb-6">
            Hoş geldiniz <strong>{form.fullName}!</strong> Hesabınız başarıyla oluşturuldu ve sisteme giriş yapabilirsiniz.
          </p>
          
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-blue-900 font-medium mb-2">
              📋 Kayıt Bilgileriniz
            </p>
            <div className="text-left space-y-1 text-sm text-blue-800">
              <p><strong>Ad Soyad:</strong> {form.fullName}</p>
              <p><strong>Telefon:</strong> +90 {form.phoneNumber}</p>
              <p><strong>TC No:</strong> {form.tcNo}</p>
              <p><strong>Çekici:</strong> {form.tractorPlate}</p>
              <p><strong>Dorse:</strong> {form.trailerPlate} ({TRAILER_TYPES.find(t => t.value === form.trailerType)?.label})</p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-900">
              ✅ <strong>Durum:</strong> Aktif - Teklif verebilirsiniz!
            </p>
          </div>

          <button
            onClick={() => navigate('/driver-mobile-home')}
            className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold text-lg shadow-lg"
          >
            Teklif Paneline Geç →
          </button>
        </div>
      </div>
    );
  }

  const getButtonClass = () => {
    if (loading || !consents.contract) {
      return 'bg-gray-300 text-gray-500 cursor-not-allowed';
    }
    return 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl';
  };

  const getButtonText = () => {
    if (loading) return 'Kaydediliyor...';
    if (!consents.contract) return '🔒 Önce Kayıt Sözleşmesini Kabul Edin';
    return 'Kayıt Ol ve Başvur ✓';
  };

  const toggleChannel = (channel: string) => {
    setElectronicChannels(prev =>
      prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel]
    );
  };

  return (
    <div className="safe-area-page min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 py-6 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <button
            onClick={() => navigate('/driver-mobile-auth')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Geri</span>
          </button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center border-2 border-gray-200 p-2">
              <img src={logo} alt="Doğuş Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sürücü Kaydı</h1>
              <p className="text-sm text-gray-600">+90 {form.phoneNumber}</p>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-900">
              <strong>Hoş geldiniz!</strong> Kayıt formunu doldurun, başvurunuz onaylandıktan sonra sisteme giriş yapabileceksiniz.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Kişisel Bilgiler */}
          <div className="bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Kişisel Bilgiler</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon Numarası <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg">
                  <span className="text-gray-700 font-medium">+90 {form.phoneNumber}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ad Soyad <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.fullName}
                  onChange={(e) => handleChange('fullName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Metin Özşahin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TC Kimlik No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  maxLength={11}
                  value={form.tcNo}
                  onChange={(e) => handleChange('tcNo', e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="21325044944"
                />
                {form.tcNo && form.tcNo.length !== 11 && (
                  <p className="mt-1 text-xs text-red-600">TC Kimlik No 11 haneli olmalıdır</p>
                )}
              </div>
            </div>
          </div>

          {/* Araç Bilgileri */}
          <div className="bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Araç Bilgileri</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Çekici Plaka <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.tractorPlate}
                  onChange={(e) => handleChange('tractorPlate', e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="01FBN80"
                  maxLength={10}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dorse Plaka <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.trailerPlate}
                  onChange={(e) => handleChange('trailerPlate', e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="34TCG80"
                  maxLength={10}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dorse Tipi (Sadece TIR) <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {TRAILER_TYPES.map((type) => (
                    <label
                      key={type.value}
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        form.trailerType === type.value
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="trailerType"
                        value={type.value}
                        checked={form.trailerType === type.value}
                        onChange={(e) => handleChange('trailerType', e.target.value)}
                        className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                        required
                      />
                      <span className="text-2xl">{type.icon}</span>
                      <span className={`flex-1 font-medium ${
                        form.trailerType === type.value ? 'text-blue-900' : 'text-gray-700'
                      }`}>
                        {type.label}
                      </span>
                      {form.trailerType === type.value && (
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Onay ve Rıza */}
          <div className="bg-white rounded-xl shadow-lg p-5">
            <div className="mb-4">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Sözleşme ve Rıza Onayları</h2>
              <p className="text-xs text-gray-500">
                Kayıt sözleşmesini okumadan kayıt olamazsınız. Açık rıza ve ticari ileti onayı opsiyoneldir.
              </p>
            </div>

            {/* Kayıt Sözleşmesi */}
            <div className="mb-3">
              <button
                type="button"
                onClick={() => setActiveModal('contract')}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg hover:border-blue-500 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-900 text-left">Kayıt Sözleşmesini Oku</span>
                </div>
                <span className="text-blue-600 text-sm">Görüntüle →</span>
              </button>
            </div>

            <label
              onClick={(e) => {
                if (!consents.contract) {
                  e.preventDefault();
                  setActiveModal('contract');
                }
              }}
              className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border-2 border-blue-200 mb-4 cursor-pointer hover:bg-blue-100 transition-colors"
            >
              <input
                type="checkbox"
                checked={consents.contract}
                onChange={(e) => {
                  e.stopPropagation();
                  if (!consents.contract) {
                    setActiveModal('contract');
                  } else {
                    setConsents(prev => ({ ...prev, contract: false }));
                  }
                }}
                className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 pointer-events-none"
                required
              />
              <span className="text-sm text-gray-900 leading-relaxed">
                Yukarıdaki <strong>Kayıt Sözleşmesi</strong>'ni okudum, anladım ve <strong>kabul ediyorum</strong>.
              </span>
            </label>

            {/* Açık Rıza */}
            <button
              type="button"
              onClick={() => setActiveModal('explicit')}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg hover:border-purple-500 transition-colors mb-3"
            >
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-gray-900 text-left">Açık Rıza Metnini Oku</span>
              </div>
              <span className="text-purple-600 text-sm">Görüntüle →</span>
            </button>

            <label
              onClick={(e) => {
                if (!consents.explicit) {
                  e.preventDefault();
                  setActiveModal('explicit');
                }
              }}
              className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border-2 border-purple-200 mb-4 cursor-pointer hover:bg-purple-100 transition-colors"
            >
              <input
                type="checkbox"
                checked={consents.explicit}
                onChange={(e) => {
                  e.stopPropagation();
                  setConsents(prev => ({ ...prev, explicit: !prev.explicit }));
                }}
                className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-900 leading-relaxed">
                <strong>Açık Rıza Metni</strong>'ni okudum; arka plan konum verimin işlenerek konuma bağlı
                yük fırsatları sunulmasına <strong>açık rıza veriyorum</strong> <span className="text-gray-500">(opsiyonel)</span>.
              </span>
            </label>

            {/* Ticari Elektronik İleti */}
            <button
              type="button"
              onClick={() => setActiveModal('electronic')}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg hover:border-teal-500 transition-colors mb-3"
            >
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-teal-600" />
                <span className="font-medium text-gray-900 text-left">Ticari Elektronik İleti Onayını Oku</span>
              </div>
              <span className="text-teal-600 text-sm">Görüntüle →</span>
            </button>

            <label
              onClick={(e) => {
                if (!consents.electronic) {
                  e.preventDefault();
                  setActiveModal('electronic');
                }
              }}
              className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg border-2 border-teal-200 mb-3 cursor-pointer hover:bg-teal-100 transition-colors"
            >
              <input
                type="checkbox"
                checked={consents.electronic}
                onChange={(e) => {
                  e.stopPropagation();
                  setConsents(prev => ({ ...prev, electronic: !prev.electronic }));
                }}
                className="mt-1 w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-900 leading-relaxed">
                <strong>Ticari Elektronik İleti Onayı</strong> metnini okudum; seçtiğim kanallar üzerinden
                ticari elektronik ileti gönderilmesine <strong>onay veriyorum</strong> <span className="text-gray-500">(opsiyonel)</span>.
              </span>
            </label>

            {consents.electronic && (
              <div className="p-4 bg-teal-50 rounded-lg border-2 border-teal-200 mb-4">
                <p className="text-sm font-medium text-gray-900 mb-2">İletişim Kanalı Tercihiniz:</p>
                <div className="space-y-2">
                  {ELECTRONIC_CHANNELS.map((channel) => (
                    <label key={channel.value} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={electronicChannels.includes(channel.value)}
                        onChange={() => toggleChannel(channel.value)}
                        className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
                      />
                      <span className="text-sm text-gray-900">{channel.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-xs text-gray-600 space-y-1">
              <p>
                {consents.contract
                  ? '✅ Kayıt sözleşmesi kabul edildi.'
                  : '⚠️ Devam etmek için sözleşmeyi okumalı ve kabul etmelisiniz.'}
              </p>
              <p>
                {consents.explicit
                  ? '🛰️ Açık rıza verildi: arka plan konum verisiyle anlık yük fırsatları sunulacak.'
                  : '🛰️ Açık rıza verilmedi: kayıt işleminiz yine de tamamlanır.'}
              </p>
              <p>
                {consents.electronic
                  ? `📲 Ticari ileti onayı verildi: ${electronicChannels.length > 0 ? electronicChannels.join(', ') : 'kanal seçilmedi'}.`
                  : '📲 Ticari ileti onayı verilmedi: kayıt işleminiz yine de tamamlanır.'}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !consents.contract}
              className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${getButtonClass()}`}
            >
              {getButtonText()}
            </button>
          </div>
        </form>

        {/* Consent Modal */}
        {activeModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    {(() => {
                      const Icon = CONSENT_META[activeModal].icon;
                      return <Icon className="w-6 h-6 text-blue-600" />;
                    })()}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{CONSENT_META[activeModal].title}</h3>
                </div>
                <button
                  onClick={() => setActiveModal(null)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {activeModal === 'contract'
                    ? personalizeText(contract)
                    : activeModal === 'explicit'
                      ? personalizeText(explicitConsentText)
                      : personalizeText(electronicConsentText)}
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row gap-3">
                  {activeModal === 'contract' ? (
                    <button
                      onClick={() => {
                        setConsents(prev => ({ ...prev, contract: true }));
                        setActiveModal(null);
                      }}
                      className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-bold"
                    >
                      ✓ Kabul Ediyorum
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setConsents(prev => ({ ...prev, [activeModal === 'explicit' ? 'explicit' : 'electronic']: true }));
                          setActiveModal(null);
                        }}
                        className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-bold"
                      >
                        ✓ Onaylıyorum
                      </button>
                      <button
                        onClick={() => {
                          setConsents(prev => ({ ...prev, [activeModal === 'explicit' ? 'explicit' : 'electronic']: false }));
                          setActiveModal(null);
                        }}
                        className="flex-1 px-6 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium"
                      >
                        Kabul Etmiyorum
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setActiveModal(null)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}